import { useRef } from 'react';

import { cn } from '@poseidon/components/cn';
import { Button } from '@poseidon/components/Input2';
import { General } from '@poseidon/components/StrokeIcons';

import type { SequenceStep, SequenceStepType } from '~/components/Sequence/sequenceSteps';
import type { WaitUnit } from '~/components/Sequence/WaitDurationInput';
import { SequenceBranchLine } from '~/components/Sequence/SequenceBranchLine';
import { SwitchRangeChip } from '~/components/Sequence/SwitchRangeChip';
import { newSequenceStep, newSwitchBranch, stepOpensEditorOnInsert, SWITCH_FAN_COL_W } from '~/components/Sequence/sequenceSteps';

interface Props {
  /** The `switch` container step being authored. */
  step: SequenceStep;
  /** Persist the updated switch step (the editor autosaves it). */
  onChange: (next: SequenceStep) => void;
  /** Remove the whole switch node from the sequence (fired when its last branch is removed). */
  onRemove?: () => void;
  /** Open the content editor for branch `branchIndex`'s step at `armIndex` (a message / connect note / …). */
  onEditBranchStep?: (branchIndex: number, armIndex: number) => void;
  /** Open the first branch's first insert picker once on mount (a just-inserted switch lands ready). */
  autoOpenPicker?: boolean;
  /** Fires when any branch's insert picker opens / closes (so the editor can raise this node's wrapper). */
  onPickerOpenChange?: (open: boolean) => void;
  /** Locked view (the per-contact contact-detail graph): freeze structure — no add-branch / remove-branch,
   *  static ranges, no in-branch insert / remove / duration edits. Editing a branch step's content still works. */
  readOnly?: boolean;
}

// Fan geometry (shared column pitch so the graph editor's stem/reservation stay aligned with this SVG).
const COL_W = SWITCH_FAN_COL_W;
const CONNECTOR_H = 26; // height of the connector band above the chips (stem → distributor → drops)
const DIST_Y = 13; // y of the horizontal distributor within that band
const STROKE = 'stroke-lynch-300 dark:stroke-slate-600';
/** Center x (block coords) of route-branch column `c`. */
const colCenter = (c: number) => c * COL_W + COL_W / 2;

/**
 * Authoring UI for a `switch`'s route branches — the fan that hangs BELOW the switch node (whose Age/~AUM
 * attribute toggle lives on the spine pill, see {@link SequenceSwitchNode}). Each branch is a fixed-width
 * column: a click-to-edit range chip (or the "Else" catch-all) over its own vertical step sub-line
 * ({@link SequenceBranchLine}) — a matching contact routes into exactly ONE branch (terminal, no rejoin).
 * A small SVG draws the tree connecting the node down into every column, plus a dashed "+ branch" tile.
 * Controlled — every edit hands a new switch step back via `onChange`, which the graph editor autosaves.
 */
export function SequenceSwitchBranches({ step, onChange, onRemove, onEditBranchStep, autoOpenPicker, onPickerOpenChange, readOnly = false }: Props) {
  const sw = step.switch;
  // Track which branch lines have an open picker (by index) so the wrapper stays raised while ANY is open.
  const openLinesRef = useRef<Set<number>>(new Set());
  const reportLineOpen = (branchIndex: number, open: boolean) => {
    const s = openLinesRef.current;
    if (open === s.has(branchIndex)) return;
    if (open) s.add(branchIndex);
    else s.delete(branchIndex);
    onPickerOpenChange?.(s.size > 0);
  };

  if (sw == null) return null;
  const { attribute, branches } = sw;
  const hasCatchAll = branches.some((b) => b.isCatchAll);

  const writeSwitch = (nextBranches: typeof branches) => onChange({ ...step, switch: { attribute, branches: nextBranches } });
  const updateBranch = (bi: number, fn: (b: (typeof branches)[number]) => (typeof branches)[number]) =>
    writeSwitch(branches.map((b, i) => (i === bi ? fn(b) : b)));

  // New range branches go BEFORE the catch-all (first-match-wins; "Else" must stay last).
  const addRangeBranch = () => {
    const insertAt = hasCatchAll ? branches.length - 1 : branches.length;
    const next = branches.slice();
    next.splice(insertAt, 0, newSwitchBranch());
    writeSwitch(next);
  };
  const addCatchAll = () => writeSwitch([...branches, newSwitchBranch(true)]);
  const removeBranch = (bi: number) => {
    const next = branches.filter((_, i) => i !== bi);
    openLinesRef.current.delete(bi);
    if (next.length === 0 && onRemove != null) onRemove();
    else writeSwitch(next);
  };

  const writeArm = (bi: number, nextArm: SequenceStep[]) => updateBranch(bi, (b) => ({ ...b, arm: nextArm }));
  const insertBranchStep = (bi: number, slot: number, type: SequenceStepType) => {
    const arm = (branches[bi]?.arm ?? []).slice();
    arm.splice(slot, 0, newSequenceStep(type));
    writeArm(bi, arm);
    // Steps you almost always write into open their editor immediately (see stepOpensEditorOnInsert).
    if (onEditBranchStep != null && stepOpensEditorOnInsert(type)) onEditBranchStep(bi, slot);
  };
  const removeBranchStep = (bi: number, i: number) => writeArm(bi, (branches[bi]?.arm ?? []).filter((_, j) => j !== i));
  const setBranchWait = (bi: number, i: number, waitDays: number, waitUnit: WaitUnit) =>
    writeArm(bi, (branches[bi]?.arm ?? []).map((s, j) => (j === i ? { ...s, waitDays, waitUnit } : s)));
  const setBranchDeadline = (bi: number, i: number, deadlineDays: number) =>
    writeArm(bi, (branches[bi]?.arm ?? []).map((s, j) => (j === i ? { ...s, deadlineDays } : s)));

  // Columns, left → right: the "Everything else" slot (col 0), the range branches, then the "+ range"
  // add-tile. Col 0 is the catch-all BRANCH if one exists, otherwise a "+ Everything else" add-tile — so
  // the fallback always sits on the LEFT. The FIRST range branch is col 1 and sits directly under the
  // node (the editor anchors the whole fan on it). Each entry keeps its ORIGINAL index (`bi`) for
  // edits/removal; the underlying `branches` array is untouched (catch-all stays last) so evaluation
  // remains first-match-wins with the fallback last.
  const branchEntries = branches.map((b, bi) => ({ b, bi }));
  const displayOrder = [...branchEntries.filter((e) => e.b.isCatchAll), ...branchEntries.filter((e) => !e.b.isCatchAll)];
  // col 0 = else slot (catch-all branch OR "+ Everything else" tile), then branches, then "+ range" tile.
  const numCols = (hasCatchAll ? 0 : 1) + branches.length + 1;
  const stemX = colCenter(1); // the first range branch — directly beneath the switch node
  const lastColX = colCenter(numCols - 1);

  // No boxed panel — the branches read as a tree hanging off the spine. `data-arm-insert` stays:
  // SequenceBranchLine's outside-click test keys off it.
  return (
    <div data-arm-insert className="relative inline-flex flex-col">
      {/* Connector tree: a stem down from the node into the FIRST range (col 1), a distributor across every
          column, and a drop into each — so the fallback (left) and the "+ range" add-tile (right) hang off
          the same tree as the branches, never floating disconnected. */}
      <svg aria-hidden width={numCols * COL_W} height={CONNECTOR_H} className="pointer-events-none absolute left-0 top-0 overflow-visible">
        <line x1={stemX} y1={0} x2={stemX} y2={DIST_Y} className={STROKE} strokeWidth={2} strokeLinecap="round" />
        <line x1={colCenter(0)} y1={DIST_Y} x2={lastColX} y2={DIST_Y} className={STROKE} strokeWidth={2} strokeLinecap="round" />
        {Array.from({ length: numCols }).map((_, c) => (
          <line key={`drop-${c}`} x1={colCenter(c)} y1={DIST_Y} x2={colCenter(c)} y2={CONNECTOR_H} className={STROKE} strokeWidth={2} strokeLinecap="round" />
        ))}
      </svg>

      {/* Columns (fixed width so the SVG above lines up): [Everything-else slot] [ranges…] [+ range]. */}
      <div className="flex items-start" style={{ paddingTop: CONNECTOR_H }}>
        {/* col 0 (only when there's no catch-all yet): the "+ Everything else" add-tile, on the LEFT. */}
        {!hasCatchAll && !readOnly && (
          <div className="flex flex-col items-center" style={{ width: COL_W }}>
            <Button
              size="2xs"
              color="gray"
              tone="soft"
              aria-label="Add doesn’t-match-any-range branch"
              tooltip="The fallback branch — it catches every contact that matched no range (or has no value)"
              icon={<General.Plus />}
              onClick={addCatchAll}
              className="whitespace-nowrap rounded-lg border border-dashed border-slate-300 dark:border-slate-600">
              Doesn’t match any range
            </Button>
          </div>
        )}

        {/* Branch columns: catch-all first (leftmost), then the ranges. */}
        {displayOrder.map(({ b, bi }) => (
          <div key={`branch-${bi}`} className="group/branch flex flex-col items-center gap-1" style={{ width: COL_W }}>
            {/* The range chip (click to edit) or a static "Doesn't match any range" fallback, with a hover ✕ in its corner. */}
            <div className="relative">
              {b.isCatchAll && (
                <span className="whitespace-nowrap rounded-full bg-slate-100 px-2.5 py-0.5 text-[10px] font-semibold text-slate-500 dark:bg-slate-800 dark:text-slate-300">
                  Doesn’t match any range
                </span>
              )}
              {!b.isCatchAll && (
                <SwitchRangeChip
                  attribute={attribute}
                  branch={b}
                  onCommit={(next) => updateBranch(bi, (br) => ({ ...br, ...next }))}
                  autoEdit={autoOpenPicker === true && bi === 0}
                  readOnly={readOnly}
                />
              )}
              {!readOnly && (
                <Button
                  size="2xs"
                  color="red"
                  tone="soft"
                  aria-label="Remove branch"
                  tooltip="Remove branch"
                  icon={<General.XMark />}
                  onClick={() => removeBranch(bi)}
                  confirm={{ title: 'Remove this branch?', confirmLabel: 'Remove' }}
                  className={cn(
                    'absolute -right-2.5 -top-2 pointer-events-none opacity-0 transition-opacity',
                    'group-hover/branch:pointer-events-auto group-hover/branch:opacity-100',
                  )}
                />
              )}
            </div>

            {/* A fresh switch focuses its first branch's RANGE (autoEdit above), not a step — so the step
                line never auto-opens its picker here; steps get added after the range is set. */}
            <SequenceBranchLine
              steps={b.arm}
              emptyHint="" // a range alone is a complete branch — steps are optional, so don't nag "add a step"
              onInsert={readOnly ? undefined : (slot, type) => insertBranchStep(bi, slot, type)}
              onRemoveAt={readOnly ? undefined : (i) => removeBranchStep(bi, i)}
              onEditAt={onEditBranchStep != null ? (i) => onEditBranchStep(bi, i) : undefined}
              onChangeWaitAt={readOnly ? undefined : (i, days, unit) => setBranchWait(bi, i, days, unit)}
              onChangeDeadlineAt={readOnly ? undefined : (i, days) => setBranchDeadline(bi, i, days)}
              onPickerOpenChange={(open) => reportLineOpen(bi, open)}
              readOnly={readOnly}
            />
          </div>
        ))}

        {/* Last column: the "+ range" add-tile, on the RIGHT — more ranges fan out this way. */}
        {!readOnly && (
          <div className="flex flex-col items-center" style={{ width: COL_W }}>
            <Button
              size="2xs"
              color="yellow"
              tone="soft"
              aria-label="Add range branch"
              tooltip="A range routes contacts whose value falls inside a band you set (e.g. $0–$100K)"
              icon={<General.Plus />}
              onClick={addRangeBranch}
              className="rounded-lg border border-dashed border-amber-300 dark:border-amber-500/40">
              range
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
